import { UseFormReturn } from "react-hook-form";
import { ReviewSection, ReviewField } from "@/components/shared/ReviewSection";
import { formatDateOnly } from "@/utils/dates";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type CoreData = {
  [key: string]: any;
};

const nestedCardStyle: React.CSSProperties = {
  border: "1px solid #e2e5e9",
  borderRadius: "8px",
  padding: "20px",
  backgroundColor: "#fafbfc",
};

const recordTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#1e5a78",
  marginBottom: "16px",
  paddingBottom: "10px",
  borderBottom: "2px solid #1e5a78",
};

const sectionSubheaderStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#293038",
  paddingLeft: "12px",
  borderLeft: "3px solid #1e5a78",
};

type AssignmentReviewPanelProps = {
  assignmentForm: UseFormReturn<any>;
  preschools: PreschoolSummary[];
  coreData: CoreData;
  getOptionText: (list: any[], value: string | null | undefined) => string;
};

function AssignmentReviewPanel({
  assignmentForm,
  preschools,
  coreData,
  getOptionText,
}: AssignmentReviewPanelProps) {
  return (
    <div className="row">
      <ReviewField
        label="Preschool"
        value={
          preschools.find(
            (p) => p.PreschoolCDSCode === assignmentForm.watch("PreschoolCode")
          )?.PreschoolName
        }
      />
      <ReviewField
        label="Program Year"
        value={assignmentForm.watch("ProgramYear")}
      />
      <ReviewField
        label="Staff Local ID"
        value={assignmentForm.watch("LocalStaffId")}
      />

      {/* Classroom Roles Section Header */}
      <div className="col-12 mt-4 mb-3">
        <div style={sectionSubheaderStyle}>Classroom Roles</div>
      </div>

      <ReviewField
        label="Role 1"
        value={getOptionText(
          coreData.staffRoleList,
          assignmentForm.watch("ClassroomRole1")
        )}
      />
      <ReviewField
        label="Role 1 Full-Time Equivalency"
        value={assignmentForm.watch("ClassroomRole1PercentOfTime")}
      />
      <div className="col-md-4"></div>

      <ReviewField
        label="Role 2"
        value={getOptionText(
          coreData.staffRoleList,
          assignmentForm.watch("ClassroomRole2")
        )}
      />
      <ReviewField
        label="Role 2 Full-Time Equivalency"
        value={assignmentForm.watch("ClassroomRole2PercentOfTime")}
      />
      <div className="col-md-4"></div>

      {/* Non-Classroom Roles Section Header */}
      <div className="col-12 mt-4 mb-3">
        <div style={sectionSubheaderStyle}>Non-Classroom Roles</div>
      </div>

      <ReviewField
        label="Role 1"
        value={getOptionText(
          coreData.nonClassroomRoleList,
          assignmentForm.watch("NonClassroomRole1")
        )}
      />
      <ReviewField
        label="Role 1 Full-Time Equivalency"
        value={assignmentForm.watch("NonClassroomRole1PercentOfTime")}
      />
      <div className="col-md-4"></div>

      <ReviewField
        label="Role 2"
        value={getOptionText(
          coreData.nonClassroomRoleList,
          assignmentForm.watch("NonClassroomRole2")
        )}
      />
      <ReviewField
        label="Role 2 Full-Time Equivalency"
        value={assignmentForm.watch("NonClassroomRole2PercentOfTime")}
      />
      <div className="col-md-4"></div>

      <ReviewField
        label="Role 3"
        value={getOptionText(
          coreData.nonClassroomRoleList,
          assignmentForm.watch("NonClassroomRole3")
        )}
      />
      <ReviewField
        label="Role 3 Full-Time Equivalency"
        value={assignmentForm.watch("NonClassroomRole3PercentOfTime")}
      />
      <div className="col-md-4"></div>
    </div>
  );
}

type ClassroomReviewPanelProps = {
  classroomForm: UseFormReturn<any>;
  preschools: PreschoolSummary[];
};

function ClassroomReviewPanel({
  classroomForm,
  preschools,
}: ClassroomReviewPanelProps) {
  return (
    <div className="row">
      <ReviewField
        label="Preschool"
        value={
          preschools.find(
            (p) => p.PreschoolCDSCode === classroomForm.watch("PreschoolCode")
          )?.PreschoolName
        }
      />
      <ReviewField
        label="Program Year"
        value={classroomForm.watch("ProgramYear")}
      />
      <ReviewField
        label="Staff Local ID"
        value={classroomForm.watch("LocalStaffId")}
      />
      <ReviewField
        label="Classroom ID"
        value={classroomForm.watch("LocalClassroomId")}
      />
    </div>
  );
}

interface StaffReviewStepsProps {
  form: UseFormReturn<any>;
  assignmentForms: UseFormReturn<any>[];
  activeAssignmentCount: number;
  classroomForms: UseFormReturn<any>[];
  activeClassroomCount: number;
  preschools: PreschoolSummary[];
  coreData: CoreData;
  skipAssignment: boolean;
  skipClassroom: boolean;
  onEditStep: (stepNumber: number) => void;
  // Permission flags - default to true for backward compatibility
  canEditAssignment?: boolean;
  canEditClassroom?: boolean;
}

export function StaffReviewSteps({
  form,
  assignmentForms,
  activeAssignmentCount,
  classroomForms,
  activeClassroomCount,
  preschools,
  coreData,
  skipAssignment,
  skipClassroom,
  onEditStep,
  canEditAssignment = true,
  canEditClassroom = true,
}: StaffReviewStepsProps) {
  const getYesNoText = (value: string | null | undefined): string => {
    if (value === null || value === undefined || value === "__UNSET__")
      return "—";
    if (value === "Y") return "Yes";
    if (value === "N") return "No";
    if (value === "") return "No Response";
    return value;
  };

  const getGenderText = (genderCode: string | null | undefined): string => {
    if (!genderCode) return "—";
    const genderMap: Record<string, string> = {
      M: "Male",
      F: "Female",
      X: "Nonbinary",
    };
    return genderMap[genderCode] || genderCode;
  };

  const getOptionText = (
    list: any[],
    value: string | null | undefined
  ): string => {
    if (!value) return "—";
    const option = list?.find((item: any) => item.Value === value);
    return option?.Text || value;
  };

  const getMultipleRaces = (): string => {
    const races = [
      form.watch("StaffRace1"),
      form.watch("StaffRace2"),
      form.watch("StaffRace3"),
      form.watch("StaffRace4"),
      form.watch("StaffRace5"),
    ].filter(Boolean);

    if (races.length === 0) return "—";

    return races
      .map((raceCode) => {
        const race = coreData.raceList?.find((r: any) => r.Value === raceCode);
        return race?.Text || raceCode;
      })
      .join(", ");
  };

  const getMultipleLanguages = (): string => {
    const languages = [
      form.watch("StaffLanguage1"),
      form.watch("StaffLanguage2"),
      form.watch("StaffLanguage3"),
      form.watch("StaffLanguage4"),
      form.watch("StaffLanguage5"),
    ].filter(Boolean);

    if (languages.length === 0) return "—";

    return languages
      .map((langCode) => {
        const lang = coreData.languagesList?.find(
          (l: any) => l.Value === langCode
        );
        return lang?.Text || langCode;
      })
      .join(", ");
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <i
            className="bi bi-clipboard-check"
            style={{ fontSize: "18px", color: "#1e5a78" }}
          ></i>
          <h2
            className="mb-0"
            style={{ fontWeight: 600, fontSize: "16px", lineHeight: 1 }}
          >
            Review & Submit
          </h2>
        </div>
        <p
          className="mb-0 text-muted"
          style={{ fontSize: "13px", paddingLeft: "26px" }}
        >
          Please review all information before submitting.
        </p>
      </div>

      <ReviewSection
        icon={<i className="bi bi-person"></i>}
        title="Staff Demographics"
        onEdit={() => onEditStep(2)}
      >
        <div className="row">
          {/* Staff Information */}
          <ReviewField
            label="Staff Local ID"
            value={form.watch("LocalStaffId")}
          />
          <ReviewField label="SEID" value={form.watch("SEID")} />
          <ReviewField label="Program Year" value={form.watch("ProgramYear")} />
          <ReviewField
            label="Effective Start Date"
            value={
              form.watch("EffectiveStartDate")
                ? formatDateOnly(form.watch("EffectiveStartDate"))
                : undefined
            }
          />

          {/* Personal Information Section Header */}
          <div className="col-12 mt-4 mb-3">
            <div style={sectionSubheaderStyle}>Personal Information</div>
          </div>

          <ReviewField
            label="First Name"
            value={form.watch("StaffLegalFirstName")}
          />
          <ReviewField
            label="Middle Name"
            value={form.watch("StaffLegalMiddleName")}
          />
          <ReviewField
            label="Last Name"
            value={form.watch("StaffLegalLastName")}
          />
          <ReviewField
            label="Date of Birth"
            value={
              form.watch("StaffDateOfBirth")
                ? formatDateOnly(form.watch("StaffDateOfBirth"))
                : undefined
            }
          />
          <ReviewField
            label="Gender"
            value={getGenderText(form.watch("StaffGender"))}
          />
          <ReviewField
            label="Hispanic"
            value={getYesNoText(form.watch("StaffEthnicity"))}
          />
          <ReviewField label="Race(s)" value={getMultipleRaces()} />
          <ReviewField
            label="Language(s) Proficient"
            value={getMultipleLanguages()}
          />
          <ReviewField
            label="Highest Degree Level"
            value={getOptionText(
              coreData.educationList,
              form.watch("StaffHighestDegreeCode")
            )}
          />

          {/* Employment Information Section Header */}
          <div className="col-12 mt-4 mb-3">
            <div style={sectionSubheaderStyle}>Employment Information</div>
          </div>

          <ReviewField
            label="Waiver?"
            value={getYesNoText(form.watch("StaffWaiver"))}
          />
          <ReviewField
            label="Waiver Issued Date"
            value={
              form.watch("StaffWaiverIssuedDate")
                ? formatDateOnly(form.watch("StaffWaiverIssuedDate"))
                : undefined
            }
          />
          <ReviewField
            label="Waiver Expiration Date"
            value={
              form.watch("StaffWaiverExpirationDate")
                ? formatDateOnly(form.watch("StaffWaiverExpirationDate"))
                : undefined
            }
          />
          <ReviewField
            label="Permit/Credential Type"
            value={getOptionText(
              coreData.staffPermitList,
              form.watch("StaffPermitOrCredentialType")
            )}
          />
          <ReviewField
            label="Permit/Credential Number"
            value={form.watch("StaffPermitOrCredentialNumber")}
          />
          <ReviewField
            label="Permit Issuance Date"
            value={
              form.watch("StaffPermitIssuedDate")
                ? formatDateOnly(form.watch("StaffPermitIssuedDate"))
                : undefined
            }
          />
          <ReviewField
            label="Permit Expiration Date"
            value={
              form.watch("StaffPermitExpirationDate")
                ? formatDateOnly(form.watch("StaffPermitExpirationDate"))
                : undefined
            }
          />
          <ReviewField
            label="Current Employment Start Date"
            value={
              form.watch("StaffCurrentEmploymentStartDate")
                ? formatDateOnly(form.watch("StaffCurrentEmploymentStartDate"))
                : undefined
            }
          />
          <ReviewField
            label="Current Employment End Date"
            value={
              form.watch("StaffCurrentEmploymentEndDate")
                ? formatDateOnly(form.watch("StaffCurrentEmploymentEndDate"))
                : undefined
            }
          />
          <ReviewField
            label="Overall Employment Start Date"
            value={
              form.watch("StaffOverallEmploymentStartDate")
                ? formatDateOnly(form.watch("StaffOverallEmploymentStartDate"))
                : undefined
            }
          />
        </div>
      </ReviewSection>

      {/* Only show Staff Assignment section if user has permission */}
      {canEditAssignment && (
        <ReviewSection
          icon={<i className="bi bi-briefcase"></i>}
          title="Staff Assignment"
          onEdit={() => onEditStep(3)}
          skipped={skipAssignment}
        >
          {!skipAssignment && (
            <div>
              {assignmentForms.slice(0, activeAssignmentCount).map((f, i) => {
                const isMulti = activeAssignmentCount > 1;
                return (
                  <div
                    key={i}
                    style={
                      isMulti
                        ? {
                            ...nestedCardStyle,
                            marginBottom:
                              i < activeAssignmentCount - 1 ? "20px" : 0,
                          }
                        : undefined
                    }
                  >
                    {isMulti && (
                      <div style={recordTitleStyle}>
                        Staff Assignment {i + 1}
                      </div>
                    )}
                    <AssignmentReviewPanel
                      assignmentForm={f}
                      preschools={preschools}
                      coreData={coreData}
                      getOptionText={getOptionText}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </ReviewSection>
      )}

      {/* Only show Staff Classroom section if user has permission */}
      {canEditClassroom && (
        <ReviewSection
          icon={<i className="bi bi-door-open"></i>}
          title="Staff Classroom"
          onEdit={() => onEditStep(4)}
          skipped={skipClassroom}
        >
          {!skipClassroom && (
            <div>
              {classroomForms.slice(0, activeClassroomCount).map((f, i) => {
                const isMulti = activeClassroomCount > 1;
                return (
                  <div
                    key={i}
                    style={
                      isMulti
                        ? {
                            ...nestedCardStyle,
                            marginBottom:
                              i < activeClassroomCount - 1 ? "20px" : 0,
                          }
                        : undefined
                    }
                  >
                    {isMulti && (
                      <div style={recordTitleStyle}>
                        Staff Classroom {i + 1}
                      </div>
                    )}
                    <ClassroomReviewPanel
                      classroomForm={f}
                      preschools={preschools}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </ReviewSection>
      )}
    </div>
  );
}
